import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Alert, 
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { Upload, Eye, Trash2, FileText, Calendar, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

const BankStatementManager = () => {
  const [loading, setLoading] = useState(false);
  const [statements, setStatements] = useState([]);
  const [missingUsers, setMissingUsers] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchStatementsForPeriod();
  }, [selectedMonth, selectedYear]);

  const fetchStatementsForPeriod = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getBranchStatements(selectedMonth, selectedYear);
      setStatements(response.statements || []);
      setMissingUsers(response.missingUsers || []);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching statements:', err);
      setError(`Error fetching statements: ${err.message}`);
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedUser || !selectedFile) {
      setError('Please select a user and file');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('userId', selectedUser);
    formData.append('month', selectedMonth);
    formData.append('year', selectedYear);

    try {
      setUploadLoading(true);
      setError(null);
      
      await apiService.uploadBankStatement(formData);
      
      setSuccess('Bank statement uploaded successfully');
      setUploadDialog(false);
      setSelectedFile(null);
      setSelectedUser('');
      
      // Refresh the list
      await fetchStatementsForPeriod();
      
      setUploadLoading(false);
    } catch (err) {
      console.error('Error uploading statement:', err);
      setError(`Error uploading statement: ${err.message}`);
      setUploadLoading(false);
    }
  };

  const handleViewStatement = async (statement) => {
    try {
      if (statement.file_url) {
        window.open(statement.file_url, '_blank');
      } else {
        setError('Unable to access the bank statement file');
      }
    } catch (err) {
      console.error('Error viewing statement:', err);
      setError(`Error viewing statement: ${err.message}`);
    }
  };

  const handleDeleteStatement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bank statement?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiService.deleteBankStatement(id);
      
      setSuccess('Bank statement deleted successfully');
      
      // Refresh the list
      await fetchStatementsForPeriod();
      
      setLoading(false);
    } catch (err) {
      console.error('Error deleting statement:', err);
      setError(`Error deleting statement: ${err.message}`);
      setLoading(false);
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 3 }, (_, i) => {
    const year = new Date().getFullYear() - 1 + i;
    return { value: year, label: year.toString() };
  });

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Bank Statement Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Calendar size={20} style={{ marginRight: 8 }} />
            <Typography variant="subtitle1" sx={{ mr: 2 }}>Period:</Typography>
          </Box>
          
          <TextField
            select
            label="Month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            sx={{ minWidth: 150, mr: 2 }}
          >
            {months.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </TextField>
          
          <TextField
            select
            label="Year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            {years.map((year) => (
              <MenuItem key={year.value} value={year.value}>
                {year.label}
              </MenuItem>
            ))}
          </TextField>
          
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={() => setUploadDialog(true)}
            sx={{ ml: 'auto' }}
          >
            Upload Statement
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Technician</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Uploaded By</TableCell>
                    <TableCell>Uploaded Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statements.length > 0 ? (
                    statements.map((statement) => (
                      <TableRow key={statement.id}>
                        <TableCell>{statement.technician_name}</TableCell>
                        <TableCell>{statement.technician_email}</TableCell>
                        <TableCell>{statement.uploaded_by_name}</TableCell>
                        <TableCell>
                          {format(new Date(statement.uploaded_at), 'MM/dd/yyyy h:mm a')}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Tooltip title="View Statement">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleViewStatement(statement)}
                              >
                                <Eye size={18} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Statement">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteStatement(statement.id)}
                              >
                                <Trash2 size={18} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No bank statements uploaded for this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {missingUsers.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AlertCircle size={20} color="orange" style={{ marginRight: 8 }} />
                  <Typography variant="subtitle1">
                    Users Missing Bank Statements ({missingUsers.length})
                  </Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Technician</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {missingUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.username}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell align="right">
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Upload size={16} />}
                              onClick={() => {
                                setSelectedUser(user.id);
                                setUploadDialog(true);
                              }}
                            >
                              Upload Statement
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialog}
        onClose={() => !uploadLoading && setUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Bank Statement</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Select User"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                {missingUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.username}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Month"
                value={selectedMonth}
                disabled
              >
                {months.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Year"
                value={selectedYear}
                disabled
              >
                {years.map((year) => (
                  <MenuItem key={year.value} value={year.value}>
                    {year.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <input
                accept=".pdf,.xls,.xlsx"
                style={{ display: 'none' }}
                id="bank-statement-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="bank-statement-upload">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={uploadLoading ? <CircularProgress size={20} /> : <Upload size={16} />}
                >
                  {selectedFile ? selectedFile.name : 'Select Bank Statement File'}
                </Button>
              </label>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Accepted formats: PDF, XLS, XLSX (max 10MB)
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setUploadDialog(false)} 
            disabled={uploadLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploadLoading || !selectedFile || !selectedUser}
            startIcon={uploadLoading ? <CircularProgress size={20} /> : <Upload size={20} />}
          >
            {uploadLoading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankStatementManager;